/**
 * Lightweight, dependency-free SVG charts for the admin analytics section:
 * a monthly meetings-over-time line chart and a horizontal user-roles bar chart.
 * Mirrors the hand-rolled SVG approach in features/companies/BillingCharts.tsx
 * (Google palette, "nice" axis ceilings, native <title> tooltips) but formats
 * plain counts instead of money.
 */

const PALETTE = [
  "#4285F4",
  "#34A853",
  "#FBBC04",
  "#EA4335",
  "#A142F4",
  "#24C1E0",
  "#FF6D01",
  "#9AA0A6",
  "#F538A0",
  "#1E8E3E",
  "#C5221F",
];

const AXIS = "var(--border, #e0e0e0)";
const GRID = "var(--border-soft, #eef0f3)";
const TXT = "var(--text-4, #9aa0a6)";

/** Round a max value up to a "nice" tick (1/2/5 × 10ⁿ) for clean gridlines. */
function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const pow = 10 ** Math.floor(Math.log10(v));
  const n = v / pow;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return step * pow;
}

type Pt = { x: number; y: number };

/** Catmull-Rom → cubic-bezier smoothing for a soft trend line. */
function smoothPath(pts: Pt[]): string {
  const first = pts[0];
  if (!first) return "";
  if (pts.length === 1) return `M${first.x} ${first.y}`;
  let d = `M${first.x} ${first.y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i] as Pt;
    const p2 = pts[i + 1] as Pt;
    const p0 = pts[i - 1] ?? p1;
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${c1x} ${c1y} ${c2x} ${c2y} ${p2.x} ${p2.y}`;
  }
  return d;
}

/** Prettify a "YYYY-MM" month label to e.g. "Aug '26". */
function monthLabel(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  const name = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
    m - 1
  ];
  return `${name} '${String(y).slice(2)}`;
}

export function MeetingsLineChart({ data }: { data: { label: string; count: number }[] }) {
  if (data.length === 0) {
    return <div style={{ color: "var(--text-4)", fontSize: 13 }}>No meetings yet.</div>;
  }
  const W = 640;
  const H = 260;
  const pad = { l: 40, r: 16, t: 12, b: 34 };
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  const n = data.length;
  const maxTick = niceCeil(Math.max(1, ...data.map((d) => d.count)));
  const xs = (i: number) => pad.l + (n === 1 ? plotW / 2 : (i * plotW) / (n - 1));
  const ys = (v: number) => pad.t + plotH - (v / maxTick) * plotH;
  const pts = data.map((d, i) => ({ x: xs(i), y: ys(d.count) }));
  const labelStep = Math.ceil(n / 12);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ height: "auto", display: "block" }} role="img" aria-label="Meetings per month">
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <g key={f}>
          <line x1={pad.l} y1={ys(maxTick * f)} x2={W - pad.r} y2={ys(maxTick * f)} stroke={GRID} strokeWidth={1} />
          <text x={pad.l - 6} y={ys(maxTick * f) + 3} fontSize={10} fill={TXT} textAnchor="end">
            {Math.round(maxTick * f)}
          </text>
        </g>
      ))}
      <line x1={pad.l} y1={pad.t} x2={pad.l} y2={H - pad.b} stroke={AXIS} strokeWidth={1} />
      <path
        d={`${smoothPath(pts)} L${xs(n - 1)} ${ys(0)} L${xs(0)} ${ys(0)} Z`}
        fill={PALETTE[0]}
        fillOpacity={0.12}
        stroke="none"
      />
      <path d={smoothPath(pts)} fill="none" stroke={PALETTE[0]} strokeWidth={2} strokeLinejoin="round" />
      {data.map((d, i) => (
        <circle key={d.label} cx={xs(i)} cy={ys(d.count)} r={5} fill="transparent">
          <title>{`${monthLabel(d.label)}: ${d.count} meetings`}</title>
        </circle>
      ))}
      {data.map((d, i) =>
        i % labelStep === 0 ? (
          <text key={d.label} x={xs(i)} y={H - pad.b + 16} fontSize={10} fill={TXT} textAnchor="middle">
            {monthLabel(d.label)}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/** Prettify a "YYYY-MM-DD" day label to a short "Sep 3". */
function dayLabel(ymd: string): string {
  const [, m, d] = ymd.split("-").map(Number);
  if (!m || !d) return ymd;
  const name = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][
    m - 1
  ];
  return `${name} ${d}`;
}

/** Vertical bar chart of meetings per day over a fixed window (last 30 days). One bar
 * per day; labels are thinned so they don't overlap. */
export function MeetingsDailyBarChart({ data }: { data: { label: string; count: number }[] }) {
  if (data.length === 0) {
    return <div style={{ color: "var(--text-4)", fontSize: 13 }}>No meetings yet.</div>;
  }
  const W = 640;
  const H = 240;
  const pad = { l: 32, r: 12, t: 12, b: 34 };
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  const n = data.length;
  const maxTick = niceCeil(Math.max(1, ...data.map((d) => d.count)));
  const slot = plotW / n;
  const barW = Math.max(2, slot * 0.62);
  const ys = (v: number) => pad.t + plotH - (v / maxTick) * plotH;
  // Thin x labels to ~8 across the window so a 30-day strip stays readable.
  const labelStep = Math.ceil(n / 8);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ height: "auto", display: "block" }} role="img" aria-label="Meetings per day (last 30 days)">
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <g key={f}>
          <line x1={pad.l} y1={ys(maxTick * f)} x2={W - pad.r} y2={ys(maxTick * f)} stroke={GRID} strokeWidth={1} />
          <text x={pad.l - 6} y={ys(maxTick * f) + 3} fontSize={10} fill={TXT} textAnchor="end">
            {Math.round(maxTick * f)}
          </text>
        </g>
      ))}
      <line x1={pad.l} y1={pad.t} x2={pad.l} y2={H - pad.b} stroke={AXIS} strokeWidth={1} />
      {data.map((d, i) => {
        const x = pad.l + i * slot + (slot - barW) / 2;
        const y = ys(d.count);
        return (
          <rect key={d.label} x={x} y={y} width={barW} height={Math.max(0, ys(0) - y)} rx={2} fill={PALETTE[0]}>
            <title>{`${dayLabel(d.label)}: ${d.count} meeting${d.count === 1 ? "" : "s"}`}</title>
          </rect>
        );
      })}
      {data.map((d, i) =>
        i % labelStep === 0 ? (
          <text key={d.label} x={pad.l + i * slot + slot / 2} y={H - pad.b + 16} fontSize={10} fill={TXT} textAnchor="middle">
            {dayLabel(d.label)}
          </text>
        ) : null,
      )}
    </svg>
  );
}

export function RolesBarChart({ data }: { data: { label: string; count: number }[] }) {
  if (data.length === 0) {
    return <div style={{ color: "var(--text-4)", fontSize: 13 }}>No data.</div>;
  }
  const rows = data.slice(0, 12);
  const max = niceCeil(Math.max(1, ...rows.map((d) => d.count)));
  const rowH = 26;
  const labelW = 150;
  const barMax = 360;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {rows.map((d, i) => (
        <div key={d.label} style={{ display: "flex", alignItems: "center", gap: 10, height: rowH }}>
          <div
            style={{
              width: labelW,
              flexShrink: 0,
              fontSize: 12,
              color: "var(--text-2)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textAlign: "right",
            }}
            title={d.label}
          >
            {d.label}
          </div>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: `${Math.max(2, (d.count / max) * barMax)}px`,
                height: 16,
                background: PALETTE[i % PALETTE.length],
                borderRadius: 3,
              }}
              title={`${d.label}: ${d.count}`}
            />
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-1)" }}>{d.count}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
